import React from "react";

import { faSync, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { IntegrationBusinessAction, openDispatchingModal } from "actions";

import { SubtleBadge } from "components/Badge";
import { IconTextButton } from "components/Button";
import Card from "components/Card/Card";
import { Warning } from "components/ErrorMessage";
import { CollapseLabel, FormCollapse } from "components/Form";
import { ExternalLink } from "components/Link";
import { Button } from "components/Form/Button";
import { Input } from "components/Form/FormikControls";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { MediumText } from "components/Text";

import { IntegrationTypes } from "constants/integrations";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import {
  DeploymentPermissions,
  IntegrationBusinessPermissions,
} from "constants/permissions";

import {
  useHasDeploymentPermission,
  useSectionToggle,
  useXeroDeepLink,
} from "hooks";
import XeroLogo from "img/Xero";
import { hasPermission } from "lib/permissions";
import { pluralize } from "lib/pluralize";

import {
  getBusinessById,
  getIntegrationBusinessesByTypeByBusinessId,
} from "selectors";

interface XeroIntegrationCardProps {
  xeroIntegrationBusiness: IntegrationBusiness;
}

function XeroIntegrationCard({
  xeroIntegrationBusiness,
}: XeroIntegrationCardProps) {
  const { id, businessId, credentialsId, integrationId, name } =
    xeroIntegrationBusiness;

  const href = useXeroDeepLink({ credentialsId, contactId: integrationId });
  const dispatch = useDispatch();

  const hasUpdatePermission = hasPermission(
    xeroIntegrationBusiness,
    IntegrationBusinessPermissions.update,
  );

  function onClickUnlink() {
    dispatch(IntegrationBusinessAction.unlinkFromBusiness(id, businessId));
  }

  function onClickSync() {
    dispatch(IntegrationBusinessAction.synchronize(id, credentialsId));
  }

  return (
    <Card>
      <Row alignCenter justifyBetween>
        <Row alignCenter>
          {href && (
            <ExternalLink href={href} target="_blank" rel="noopener noreferrer">
              <XeroLogo width="40px" />
            </ExternalLink>
          )}
          <Column marginLeft={1}>
            <MediumText>{name}</MediumText>
          </Column>
        </Row>
        {hasUpdatePermission && (
          <Row alignCenter>
            <IconTextButton
              icon={faSync}
              onClick={onClickSync}
              tooltip="Sync with Xero"
            />
            <IconTextButton icon={faTrash} onClick={onClickUnlink} />
          </Row>
        )}
      </Row>
    </Card>
  );
}

enum IntegrationTableSection {
  XERO = "XERO",
  MYOB = "MYOB",
}

interface IntegrationsTableProps {
  businessId: string;
  isOpen: boolean;
  onToggle: () => void;
}

export const IntegrationsTable = ({
  businessId,
  onToggle,
  isOpen,
}: IntegrationsTableProps) => {
  const dispatch = useDispatch();
  const { errors } = useFormikContext<{ identicalCardIds?: string }>();

  const business = useSelector(getBusinessById(businessId)) || {};

  const xeroIntegrationBusinesses = useSelector(
    getIntegrationBusinessesByTypeByBusinessId(
      IntegrationTypes.Xero,
      businessId,
    ),
  );

  const hasMyobExportsFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureMyobIntegration,
  );

  const [section, setSection] = useSectionToggle(IntegrationTableSection.XERO);

  const hasXeroIntegrationBusiness = !!xeroIntegrationBusinesses.length;

  const businessHasMyobData =
    hasMyobExportsFeature &&
    (business.myobCustomerCardId != null ||
      business.myobSupplierCardId != null);

  const xeroIntegrationCount = xeroIntegrationBusinesses.length;
  const myobIntegrationCount = businessHasMyobData ? 1 : 0;

  const numIntegrations = xeroIntegrationCount + myobIntegrationCount;

  const header = (
    <Row>
      <SubtleBadge>Integrations</SubtleBadge>
      <CollapseLabel>
        {numIntegrations} {pluralize("Integration", numIntegrations)}
      </CollapseLabel>
    </Row>
  );

  const XeroHeader = (
    <Row>
      <SubtleBadge>Xero</SubtleBadge>
      <CollapseLabel>
        {xeroIntegrationCount} Xero{" "}
        {pluralize("Integration", xeroIntegrationCount)}
      </CollapseLabel>
      <Row alignCenter flexWrap />
    </Row>
  );

  const MyobHeader = (
    <Row>
      <SubtleBadge>MYOB</SubtleBadge>
      <CollapseLabel>
        {myobIntegrationCount} MYOB{" "}
        {pluralize("Integration", myobIntegrationCount)}
      </CollapseLabel>
      <Row alignCenter flexWrap />
    </Row>
  );

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={header}
      dataTour="integrationsCollapse"
    >
      <Column fullWidth>
        <FormCollapse
          isOpen={section === IntegrationTableSection.XERO}
          onToggle={() => setSection(IntegrationTableSection.XERO)}
          header={XeroHeader}
        >
          <Column fullWidth>
            <WaitForSync requiredData={[ApiModel.INTEGRATION_BUSINESSES]}>
              {hasXeroIntegrationBusiness ? (
                <XeroIntegrationCard
                  xeroIntegrationBusiness={xeroIntegrationBusinesses[0]}
                />
              ) : (
                <Button
                  onClick={() =>
                    dispatch(
                      openDispatchingModal(
                        ModalTypes.EditIntegrationBusiness,
                        window.location.hash,
                        {
                          integrationCredentialType: IntegrationTypes.Xero,
                          businessId,
                        },
                      ),
                    )
                  }
                >
                  Link To Xero
                </Button>
              )}
            </WaitForSync>
          </Column>
        </FormCollapse>
      </Column>
      {hasMyobExportsFeature && (
        <Column fullWidth>
          <FormCollapse
            isOpen={section === IntegrationTableSection.MYOB}
            onToggle={() => setSection(IntegrationTableSection.MYOB)}
            header={MyobHeader}
          >
            <Column fullWidth>
              <Grid item xs={12} sm={12} className="p-2">
                <Input
                  label="Customer Card Id"
                  name="myobCustomerCardId"
                  maxLength={15}
                />
              </Grid>
              <Grid item xs={12} sm={12} className="p-2">
                <Input
                  label="Supplier Card Id"
                  name="myobSupplierCardId"
                  maxLength={15}
                />
              </Grid>
              {errors.identicalCardIds && (
                <Grid item xs={12} sm={12} className="p-2">
                  <Warning>{errors.identicalCardIds}</Warning>
                </Grid>
              )}
            </Column>
          </FormCollapse>
        </Column>
      )}
    </FormCollapse>
  );
};
